import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { RouterStateSerializer } from '@ngrx/router-store';
import { HttpClient } from '@angular/common/http';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';

import {
  CONFIG_INITIALIZER, provideConfig, SiteContextConfigInitializer, ConfigInitializerService,
  OccEndpointsService, ContextServiceMap, provideFeatureToggles,
  CmsPageAdapter, PageMetaResolver,
  SiteContextUrlSerializer
} from '@spartacus/core';

import { OutletPosition, BaseStorefrontModule } from '@spartacus/storefront';

import { RecipeCategoryPageComponent } from '@seb/web-common/recipes/templates/recipe-category-page/recipe-category-page.component';
import { ProductDetailsPageComponent } from '@seb/web-common/product/templates/product-details-page/product-details-page.component';
import { RecipeDetailsPageComponent } from '@seb/web-common/recipes/templates/recipe-details-page/recipe-details-page.component';
import { SearchResultPageComponent } from '@seb/web-common/search/templates/search-result-page/search-result-page.component';
import { SubCategoryPageComponent } from '@seb/web-common/category/templates/sub-category-page/sub-category-page.component';
import { RecipeHomePageComponent } from '@seb/web-common/recipes/templates/recipe-home-page/recipe-home-page.component';
import { RecipeListPageComponent } from '@seb/web-common/recipes/templates/recipe-list-page/recipe-list-page.component';
import { AccessoryPageComponent } from '@seb/web-common/accessory/templates/accessory-page/accessory-page.component';
import { CategoryPageComponent } from '@seb/web-common/category/templates/category-page/category-page.component';
import { AccountPageComponent } from '@seb/web-common/account/templates/account-page/account-page.component';

import { BrandsEnum } from '@seb/web-design-system/core';

import { CustomSiteContextConfigInitializer } from '../config/initializers/custom-site-context-config.initializer';
import { AuthenticationConfigInitializer } from '../config/initializers/authentication-config.initializer';
import { GeolocationConfigInitializer } from '../config/initializers/geolocation-config.initializer';
import { appInitServiceMap } from '../config/initializers/service-map-config-initializer';
import { BaseConfigInitializer } from '../config/initializers/base-config.initializer';
import { I18nConfigInitializer } from '../config/initializers/i18n-config.initializer';
import { HybrisTranslateLoaderFactory } from '../config/hybris-translate.loader';

import { SalesConditionsPageComponent } from '../templates/sales-conditions-page/sales-conditions-page.component';
import { FreeContentCgvPageComponent } from '../templates/free-content-cgv-page/free-content-cgv-page.component';
import { NewGenerationPageComponent } from '../templates/new-generation-page/new-generation-page.component';
import { CompatibilityPageComponent } from '../templates/compatibility-page/compatibility-page.component';
import { StoreLocatorPageComponent } from '../templates/store-locator-page/store-locator-page.component';
import { ServicePageComponent } from '../templates/service-page/service-page.component';
import { EmptyPageComponent } from '../templates/empty-page/empty-page.component';
import { HomepageComponent } from '../templates/homepage/homepage.component';

import { CustomSiteContextUrlSerializer } from '../services/custom-site-context-url-serializer.service';
import { CustomRouterStateSerializer } from './serializers/custom-router-state.serializer';
import { RecipePageMetaResolver } from './resolvers/recipe-page-meta.resolver';
import { CustomOccCmsPageAdapter } from './adapters/custom-occ-page-adapter';
import { SpartacusFeaturesModule } from './spartacus-features.module';
import { spartacusConfig } from './spartacus-common-configuration';
import { generateOutletProviders } from '../utils';

@NgModule({
  imports: [
    BaseStorefrontModule,

    StoreModule.forRoot({}),
    EffectsModule.forRoot([]),

    SpartacusFeaturesModule,

    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HybrisTranslateLoaderFactory,
        deps: [
          ConfigInitializerService,
          OccEndpointsService,
          HttpClient
        ]
      }
    })
  ]
})
export class SpartacusModule {
  constructor(@Optional() @SkipSelf() parent: SpartacusModule) {
    if (parent) {
      throw new Error('SpartacusModule cannot be loaded twice!');
    }
  }

  static withBrand(brand: BrandsEnum): ModuleWithProviders<SpartacusModule> {
    return {
      ngModule: SpartacusModule,
      providers: [
        { provide: SiteContextConfigInitializer, useClass: CustomSiteContextConfigInitializer },
        { provide: SiteContextUrlSerializer, useClass: CustomSiteContextUrlSerializer },
        { provide: ContextServiceMap, useFactory: appInitServiceMap },

        { provide: CONFIG_INITIALIZER, useClass: AuthenticationConfigInitializer, multi: true },
        { provide: CONFIG_INITIALIZER, useClass: GeolocationConfigInitializer, multi: true },
        { provide: CONFIG_INITIALIZER, useClass: BaseConfigInitializer, multi: true },
        { provide: CONFIG_INITIALIZER, useClass: I18nConfigInitializer, multi: true },

        { provide: RouterStateSerializer, useExisting: CustomRouterStateSerializer },
        { provide: CmsPageAdapter, useExisting: CustomOccCmsPageAdapter },
        { provide: PageMetaResolver, useExisting: RecipePageMetaResolver, multi: true },

        provideFeatureToggles({
          ssrStrictErrorHandlingForHttpAndNgrx: false,
          propagateErrorsToServer: false
        }),

        provideConfig(
          spartacusConfig
        ),

        ...generateOutletProviders(brand, {
          id: 'HomePageTemplate',
          component: HomepageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'EmptyPageTemplate',
          component: EmptyPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'SubCategoryPageTemplate',
          component: SubCategoryPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'CategoryPageTemplate',
          component: CategoryPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'ProductDetailsPageTemplate',
          component: ProductDetailsPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'AccountPageTemplate',
          component: AccountPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'SearchResultPageTemplate',
          component: SearchResultPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'NewGenerationTemplate',
          component: NewGenerationPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'ServicePageTemplate',
          component: ServicePageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'StoreLocatorPageTemplate',
          component: StoreLocatorPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'SalesConditionsPageTemplate',
          component: SalesConditionsPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'FreeContentPageTemplate',
          component: FreeContentCgvPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'RepairDocumentsPageTemplate',
          component: CompatibilityPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'RecipeCategoryPageTemplate',
          component: RecipeCategoryPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'RecipeHomePageTemplate',
          component: RecipeHomePageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'RecipeListPageTemplate',
          component: RecipeListPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'RecipeDetailsPageTemplate',
          component: RecipeDetailsPageComponent,
          position: OutletPosition.REPLACE
        }),
        ...generateOutletProviders(brand, {
          id: 'AccessoriesHomePageTemplate',
          component: AccessoryPageComponent,
          position: OutletPosition.REPLACE
        })
      ]
    };
  }
}
